import { Button, UIHeading1, UIHeading2 } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { type FC } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { type TAdfoxBannerCompleteSizes } from 'shared/types/adfox';
import { Ticker } from 'shared/components/Ticker';
import { type TSpecialProjectBannerContext } from 'shared/types/bannerContext';

import styles from './SpecialProjectDesktop.css';

export type TSpecialProjectDesktopProps = {
  context: TSpecialProjectBannerContext;
  sizes: TAdfoxBannerCompleteSizes;
};

/**
 * Desktop-версия баннера спецпроекта.
 *
 * Перенесена по аналогии с frontend-serp:
 * src/shared/components/AdfoxBanner/internal/components/templates/NewbuildingSpecialProject/NewbuildingSpecialProject.tsx
 * (бегущая строка `Marquee` заменена на локальный `Ticker`).
 */
export const SpecialProjectDesktop: FC<TSpecialProjectDesktopProps> = ({ context, sizes }) => {
  const {
    bgImage,
    title,
    text,
    url,
    buttonText,
    substrateNotNeeded,
    substrateLogoNotNeeded,
    logo,
    label,
    disclaimer,
    mortgageTermsWarning,
    mortgageWarning,
  } = context;

  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      data-testid="special-project-desktop"
      href={url}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className={clsx(
        styles['container'],
        !Number(substrateNotNeeded) && styles['container--without-substrate'],
        !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
      )}
      style={{ ...sizes, backgroundImage: `url(${bgImage})` }}
    >
      <div className={styles['wrapper']}>
        <div className={styles['logo']}>
          <img className={styles['logo-image']} src={logo} alt={title} />
        </div>
        <div className={styles['content']}>
          <div className={styles['info']}>
            {!!label && (
              <div className={styles['label']}>
                <AdLabel label={label} />
              </div>
            )}
            <div className={styles['title']}>
              <UIHeading1 color="text-inverted-default">{title}</UIHeading1>
            </div>
            <div className={styles['text']}>
              <UIHeading2 color="text-inverted-default">{text}</UIHeading2>
            </div>
          </div>
          {buttonText && (
            <div className={styles['control']}>
              <Button type="button" theme="fill_white_primary">
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
      {disclaimer && (
        <div className={styles['note']}>
          <Ticker text={disclaimer} theme="light" />
        </div>
      )}
      {withMortgageAnnotations && (
        <div className={styles['mortgage-disclaimer']}>
          <MortgageAnnotations
            height={sizes.height}
            mortgageWarning={mortgageWarning}
            mortgageTermsWarning={mortgageTermsWarning}
          />
        </div>
      )}
    </a>
  );
};
